import { useLocalization } from '@magicdoorinc/ui';

export const defaultPrefixTexts: { [key: string]: string } = {
  ProratedRent: 'Prorated rent',
  SecurityDeposit: 'Security deposit',
  FailedProcessingFee: 'Failed processing fee',
  AchProcessingFee: 'ACH processing fee',
  AchReturnFee: 'ACH return fee',
  LeasePayment: 'Lease payment',
  ReturnPaymentFee: 'Return payment fee',
  ReturnPaymentProcessingFee: 'Return payment processing fee',
  DisputeFee: 'Dispute fee',
  DisputeProcessingFee: 'Dispute processing fee',
  NoSufficientFundsFee: 'No sufficient funds fee',
  TransferPropertyFunds: 'Transfer property funds',
  AchFailedFee: 'ACH failed fee',
  SecurityDepositAllocated: 'Security deposit allocated',
  SecurityDepositReleased: 'Security deposit released',
  LeaseBalanceRelease: 'Lease balance release',
  RentalApplicationFee: 'Rental application fee',
};

type DescriptionWithKey = {
  Key: string;
  [key: string]: any;
};

export type DescriptionValue = string | DescriptionWithKey | null | undefined;

const isDescriptionObject = (obj: any): obj is object => {
  return typeof obj === 'object' && obj !== null;
};

const hasValidKey = (obj: object): obj is DescriptionWithKey => {
  return 'Key' in obj && typeof (obj as any).Key === 'string';
};

const extractJsonFromString = (text: string): { json: DescriptionWithKey | null; remainingText: string } => {
  const firstBrace = text.indexOf('{');
  if (firstBrace === -1) {
    return { json: null, remainingText: text };
  }

  let depth = 0;
  let endIndex = -1;

  for (let i = firstBrace; i < text.length; i++) {
    if (text[i] === '{') {
      depth++;
    } else if (text[i] === '}') {
      depth--;
      if (depth === 0) {
        endIndex = i + 1;
        break;
      }
    }
  }

  if (endIndex === -1) {
    return { json: null, remainingText: text };
  }

  try {
    const jsonString = text.slice(firstBrace, endIndex);
    const parsedJson = JSON.parse(jsonString);
    const remainingText = text.slice(endIndex).trim();

    if (isDescriptionObject(parsedJson) && hasValidKey(parsedJson)) {
      return { json: parsedJson, remainingText };
    }
  } catch {
    // do nothing
  }
  return { json: null, remainingText: text };
};

export const getPrefixKeyValue = (key: string, customPrefixTexts?: { [key: string]: string }) => {
  const { t } = useLocalization();
  const prefixTexts = { ...defaultPrefixTexts, ...customPrefixTexts };
  return prefixTexts[key] ? t(prefixTexts[key]) : key;
};

const processStringDescription = (description: string, customPrefixTexts?: { [key: string]: string }): string => {
  const { json, remainingText } = extractJsonFromString(description);

  if (json) {
    const prefixValue = getPrefixKeyValue(json.Key, customPrefixTexts);
    return remainingText ? `${prefixValue} ${remainingText}` : prefixValue;
  }

  return description;
};

const processObjectDescription = (description: object, customPrefixTexts?: { [key: string]: string }): string => {
  if (hasValidKey(description)) {
    return getPrefixKeyValue(description.Key, customPrefixTexts);
  }
  return JSON.stringify(description);
};

export const parseKeyBasedDescription = (description: DescriptionValue, customPrefixTexts?: { [key: string]: string }): string => {
  if (typeof description === 'string') {
    return processStringDescription(description, customPrefixTexts);
  }

  if (isDescriptionObject(description)) {
    return processObjectDescription(description, customPrefixTexts);
  }

  return '';
};
